import gsap from "gsap";
import animators from "./animators";

const EXCLUDE = [
    'body > header',
    '[data-elementor-type="header"]',
    '[data-elementor-type="popup"]',
    '.elementor-location-header',
    '.elementor-location-popup',
    '.login-popup-overlay:not(.login-popup-inline)',
    '.atc-popup-overlay',
    '.sliding-cart',
    '.header__dropdown',
    '.swiper',
    '.swiper-container',
    '.slick-slider',
    '.lp-header',
].join(', ');

const reveal = {
    pending: new Map(),
    hidden: [],
    observer: null,
    ticking: false,

    init(intro) {
        const viewport = window.innerHeight;
        const first = [];
        const later = [];

        if (typeof window.IntersectionObserver !== 'function') {
            return;
        }

        this.collect().forEach((target) => {
            const rect = target.el.getBoundingClientRect();

            if (!rect.width || !rect.height || rect.bottom <= 0) {
                return;
            }

            if (rect.top < viewport) {
                if (intro && target.animator.prehide) {
                    first.push(target);
                }
                return;
            }

            later.push(target);
        });

        first.concat(later).forEach(({ el, animator }) => {
            el.classList.add('ex-reveal');
            animator.hide(el);
            this.hidden.push(el);
        });

        first.forEach(({ el, animator }, index) => {
            animator.show(el, 0.1 + Math.min(index * 0.07, 0.6));
        });

        if (later.length) {
            this.observer = new IntersectionObserver((entries) => this.enter(entries), { threshold: [0, 0.01, 0.05, 0.1, 0.2, 0.3, 0.5, 0.75, 1] });
            later.forEach(({ el, animator }) => {
                this.pending.set(el, animator);
                this.observer.observe(el);
            });
            this.onScroll = () => this.checkEnd();
            window.addEventListener('scroll', this.onScroll, { passive: true });
        }

        this.watchProducts();
    },

    collect() {
        const found = new Map();

        animators.list.forEach((animator) => {
            document.querySelectorAll(animator.selector).forEach((el) => {
                if (found.has(el) || (animator.skip && animator.skip(el)) || this.excluded(el)) {
                    return;
                }
                found.set(el, animator);
            });
        });

        return Array.from(found.keys())
            .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1))
            .map((el) => ({ el, animator: found.get(el) }));
    },

    excluded(el) {
        const parent = el.parentElement;

        return el.classList.contains('elementor-invisible')
            || !!(parent && parent.closest(EXCLUDE))
            || this.elementorMotion(el);
    },

    elementorMotion(el) {
        const raw = el.getAttribute('data-settings');

        if (!raw) {
            return false;
        }

        try {
            const settings = JSON.parse(raw);

            return Object.keys(settings).some((key) => {
                const value = settings[key];

                if (!value || value === 'none' || value === 'no') {
                    return false;
                }

                return /^_?animation(_tablet|_mobile)?$/.test(key)
                    || /^motion_fx_motion_fx_(scrolling|mouse)$/.test(key)
                    || key === 'sticky';
            });
        } catch (error) {
            return false;
        }
    },

    enter(entries) {
        const needed = window.innerHeight * 0.1;

        entries
            .filter((entry) => entry.isIntersecting && entry.intersectionRect.height >= Math.min(entry.boundingClientRect.height, needed) - 1)
            .forEach((entry, index) => this.play(entry.target, index * 0.08));
    },

    play(el, delay) {
        const animator = this.pending.get(el);

        if (!animator) {
            return;
        }

        this.pending.delete(el);
        this.observer.unobserve(el);
        animator.show(el, delay);

        if (!this.pending.size) {
            this.observer.disconnect();
            window.removeEventListener('scroll', this.onScroll);
        }
    },

    checkEnd() {
        if (this.ticking) {
            return;
        }

        this.ticking = true;
        window.requestAnimationFrame(() => {
            this.ticking = false;

            if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4) {
                Array.from(this.pending.keys()).forEach((el, index) => this.play(el, Math.min(index * 0.05, 0.4)));
            }
        });
    },

    swap(from, to) {
        const before = [from].concat(Array.from(from.querySelectorAll('*')));
        const after = [to].concat(Array.from(to.querySelectorAll('*')));

        before.forEach((el, index) => {
            const target = after[index];
            const animator = this.pending.get(el);
            const tweens = gsap.getTweensOf(el);

            if (!target || target.tagName !== el.tagName || (!animator && !tweens.length)) {
                return;
            }

            if (animator && this.observer) {
                this.pending.delete(el);
                this.observer.unobserve(el);
                target.classList.add('ex-reveal');
                animator.hide(target);
                this.hidden.push(target);
                this.pending.set(target, animator);
                this.observer.observe(target);
                return;
            }

            const wait = Math.max(0, ...tweens.map((tween) => (tween.parent ? tween.startTime() - tween.parent.time() : 0)));
            const opacity = parseFloat(el.style.opacity);

            gsap.killTweensOf(el);
            gsap.fromTo(target, {
                opacity: Number.isNaN(opacity) ? 1 : opacity,
                x: gsap.getProperty(el, 'x'),
                y: gsap.getProperty(el, 'y'),
                scale: gsap.getProperty(el, 'scale'),
                rotation: gsap.getProperty(el, 'rotation'),
            }, {
                opacity: 1,
                x: 0,
                y: 0,
                scale: 1,
                rotation: 0,
                delay: wait,
                duration: 0.7,
                clearProps: 'opacity,transform,transition',
            });
        });
    },

    reset() {
        if (this.observer) {
            this.observer.disconnect();
        }

        this.pending.clear();
        this.hidden.forEach((el) => {
            const parts = [el].concat(Array.from(el.querySelectorAll(animators.parts)));
            gsap.killTweensOf(parts);
            gsap.set(parts, { clearProps: 'opacity,transform,transformOrigin,transition' });
        });
        this.hidden = [];
    },

    watchProducts() {
        const main = document.querySelector('.shop-page__main');

        if (!main) {
            return;
        }

        new MutationObserver((mutations) => {
            const cards = [];

            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType !== 1) {
                        return;
                    }
                    if (node.matches('li.product')) {
                        cards.push(node);
                    } else {
                        cards.push(...node.querySelectorAll('li.product'));
                    }
                });
            });

            if (cards.length) {
                gsap.fromTo(cards, { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.6, stagger: 0.05, clearProps: 'opacity,transform' });
            }
        }).observe(main, { childList: true, subtree: true });
    },
};

export default reveal;
